import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useStudioWorkspace } from "../../firestoreStudio/context/StudioWorkspaceProvider";
import { useRuntimeConnection } from "../../firestoreStudio/runtime/StudioRuntimeProvider";
import { useAllStudioConnections } from "../../firestoreStudio/hooks/useStudioConnections";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import ConnectionSwitchDialog from "./ConnectionSwitchDialog";
import FirestoreRuntimeInspector from "./FirestoreRuntimeInspector";

function statusClass(status) {
  if (status === "connected") return "fa-ws-status-live";
  if (status === "connecting") return "fa-ws-status-pending";
  if (status === "error") return "fa-ws-status-error";
  return "fa-ws-status-idle";
}

function statusLabel(status, loading) {
  if (loading) return "Connecting…";
  if (status === "connected") return "Connected";
  if (status === "connecting") return "Connecting";
  if (status === "error") return "Error";
  return "Idle";
}

export default function FirestoreManagerWorkspaceBar() {
  const { workspace } = useStudioWorkspace();
  const {
    connection,
    connectionId,
    projectId,
    app,
    firestore,
    status,
    isActive,
    runtimeVersion,
    loading: runtimeLoading,
    reconnect,
  } = useRuntimeConnection();
  const {
    registrySyncing,
    rediscoverCollections,
    runtimeMismatch,
    discoverySource,
    registryStatus,
  } = useFirestoreAdmin();
  const { data: connections = [] } = useAllStudioConnections();

  const [switchOpen, setSwitchOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const activeConn = connections.find((c) => c.id === (connectionId || workspace.connectionId));
  const firebaseProjectId = projectId || activeConn?.firebaseProjectId || "—";
  const runtimeAppName = app?.name || firestore?.app?.name || "—";
  const databaseId = firestore?._databaseId?.database || "(default)";

  const onRediscover = async () => {
    setBusy(true);
    try {
      await rediscoverCollections();
    } finally {
      setBusy(false);
    }
  };

  const onRefreshRuntime = () => {
    reconnect();
  };

  return (
    <>
      <section className="fa-workspace-bar fa-card">
        <div className="fa-workspace-bar-head">
          <div>
            <p className="fa-workspace-kicker">Enterprise Workspace</p>
            <h2 className="fa-workspace-title">
              {workspace.connectionName || connection?.name || "No active connection"}
            </h2>
          </div>
          <span className={`fa-ws-pill ${statusClass(status)}`}>
            {statusLabel(status, runtimeLoading)}
          </span>
        </div>

        <div className="fa-workspace-grid">
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Firebase Project ID</span>
            <span className="fa-workspace-value mono">{firebaseProjectId}</span>
          </div>
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Environment</span>
            <span className="fa-workspace-value">{workspace.environment || activeConn?.environment || "—"}</span>
          </div>
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Runtime App Name</span>
            <span className="fa-workspace-value mono">{runtimeAppName}</span>
          </div>
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Database</span>
            <span className="fa-workspace-value mono">{databaseId}</span>
          </div>
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Runtime Version</span>
            <span className="fa-workspace-value mono">{runtimeVersion}</span>
          </div>
          <div className="fa-workspace-stat">
            <span className="fa-workspace-label">Discovery Source</span>
            <span className="fa-workspace-value">{discoverySource || "—"}</span>
          </div>
        </div>

        <div className="fa-workspace-actions">
          <button type="button" className="fa-btn fa-btn-primary" onClick={() => setSwitchOpen(true)}>
            Switch Connection
          </button>
          <button type="button" className="fa-btn" onClick={onRefreshRuntime} disabled={!workspace.connectionId}>
            Refresh Runtime
          </button>
          <button
            type="button"
            className="fa-btn"
            onClick={onRediscover}
            disabled={!isActive || registrySyncing || busy}
          >
            {registrySyncing || busy ? "Rediscovering…" : "Rediscover Collections"}
          </button>
          <button type="button" className="fa-btn" onClick={() => setInspectorOpen((v) => !v)}>
            {inspectorOpen ? "Hide Runtime Inspector" : "Open Runtime Inspector"}
          </button>
          <Link to="/admin/firestore-studio" className="fa-btn">
            Open Firestore Studio
          </Link>
        </div>

        {runtimeMismatch && (
          <div className="fa-workspace-alert">
            <strong>Runtime mismatch detected.</strong>
            <span>The active runtime does not match the workspace connection.</span>
            <div className="fa-workspace-actions mt-2">
              <button type="button" className="fa-btn fa-btn-primary" onClick={onRefreshRuntime}>
                Reload Runtime
              </button>
              <button type="button" className="fa-btn" onClick={() => setSwitchOpen(true)}>
                Switch Workspace
              </button>
            </div>
          </div>
        )}

        {!workspace.connectionId && (
          <div className="fa-workspace-alert">
            <strong>No active workspace connection.</strong>
            <span>Select a connection to browse customer Firestore data.</span>
          </div>
        )}

        {workspace.connectionId && !isActive && !runtimeLoading && status === "error" && (
          <div className="fa-workspace-alert">
            <strong>Runtime connection failed.</strong>
            <span>Refresh the runtime or switch to a different connection.</span>
          </div>
        )}

        {registryStatus && (
          <p className="fa-workspace-footnote">Registry: {registryStatus}</p>
        )}
      </section>

      {inspectorOpen && <FirestoreRuntimeInspector defaultOpen />}

      <ConnectionSwitchDialog
        open={switchOpen}
        connections={connections}
        onClose={() => setSwitchOpen(false)}
      />
    </>
  );
}
